import React, {ReactNode} from "react";
import {Dialog} from "primereact/dialog";
import PortalDialogType from "../../PortalDialogType";
import {Button} from "primereact/button";
import PortalDocument from "../../../document/PortalDocument";
import MimeTypeFilter from "../../../document/mimetype/MimeTypeFilter";
import MimeTypes from "../../../document/mimetype/MimeTypes";
import BarcodeFields from "../../field/barcode/BarcodeFields";
import {Dropdown} from "primereact/dropdown";
import {InputNumber} from "primereact/inputnumber";
import {WebServiceType, WebServiceTypes} from "@softvision/webpdf-wsclient-9-typescript";
import PortalDocumentManager from "../../../document/PortalDocumentManager";
import DocumentSettings from "../../../document/DocumentSettings";
import MimeType from "../../../document/mimetype/MimeType";
import Message from "../../../../shared/log/Message";
import {MessageType} from "../../../../shared/log/MessageInterface";
import BarcodeSwissQrCodeFormDialog from "./BarcodeSwissQrCodeFormDialog";
import BarcodeDialog, {BarcodeDialogProperties, BarcodeDialogStatus} from "./BarcodeDialog";
import UnitHelper from "../../../../shared/helper/UnitHelper";
import LengthUnit from "../../../../shared/units/LengthUnit";
import {
	Barcode,
	Information,
	Metrics,
	PagePages,
	QrSwissPaymentBarcode,
	QrSwissPaymentContent,
	QrSwissPaymentContentFormat,
	QrSwissPaymentFormat,
	QrSwissPaymentPdf,
	QrSwissPaymentPdfMode,
	QrSwissPaymentSeparator,
	Rectangle,
	Settings,
	SignatureEditProfile
} from "@softvision/webpdf-wsclient-9-typescript/lib/generated-sources";
import {PortalAddBarcode, PortalBarcodeFormat, SwissQrCodePosition} from "../../../../shared/models/PortalAddBarcode";
import {PortalBarcode} from "../../../../shared/models/PortalBarcode";
import {SwissQrCodeForm} from "../../../../shared/models/SwissQrCodeForm";
import PortalDialogSecurity, {Intention} from "../../PortalDialogSecurity";
import Translate from "../../../../shared/translation/Translate";
import Helper from "../../../../shared/components/Helper";
import {Base64} from "js-base64";
import Icon from "../../../../shared/components/Icon";
import FunctionHelper from "../../../../shared/helper/FunctionHelper";

interface BarcodeCreateSwissQrCodeDialogProperties extends BarcodeDialogProperties {
}

interface BarcodeCreateSwissQrCodeDialogStatus extends BarcodeDialogStatus {
	pages: number;
	currentPage: string;
	pageHeight: number;
}

export class BarcodeCreateSwissQrCodeDialogDefinition extends BarcodeDialog<BarcodeCreateSwissQrCodeDialogProperties,
	BarcodeCreateSwissQrCodeDialogStatus> {
	constructor(props: any) {
		super(props);

		this.state = {
			...this.state,
			pages: 1,
			currentPage: "1",
			pageHeight: 0
		};
	}

	public static isEnabled(portalInfo: Information): boolean {
		return super.isEnabled(portalInfo);
	}

	public static isActive(
		selectedDocuments: Array<PortalDocument>, portalInfo: Information, signatureEditProfile: SignatureEditProfile
	): boolean {
		let mimeTypes: Array<MimeType> = [];

		for (let selectedDocument of selectedDocuments) {
			mimeTypes.push(selectedDocument.getMimeType());

			if (selectedDocument.isLocked()) {
				return false;
			}

			if (!PortalDialogSecurity.checkRestrictions(
				PortalDialogType.BARCODE_CREATE_SWISS_QR, signatureEditProfile, Intention.editingContent,
				selectedDocument
			)) {
				return false;
			}

			if (!selectedDocument.isEditable()) {
				return false;
			}
		}

		return this.isEnabled(portalInfo) && selectedDocuments.length === 1 &&
			MimeTypeFilter.checkSelection(mimeTypes, [MimeTypes.PDF]);
	}

	protected buildPreview(parameter: PortalAddBarcode): void {
		if (this.state.previewBuild) {
			return;
		}

		this.setState({
			showPreview: true,
			previewBuild: true
		});
	}

	private getFormatHeight(format: QrSwissPaymentFormat): number {
		switch (format) {
			case QrSwissPaymentFormat.BillOnly:
				return 105;
			case QrSwissPaymentFormat.BillExtraSpace:
				return 110;
			default:
				return 0;
		}
	}

	public renderContents(): ReactNode {
		let parameter: PortalBarcode | undefined = this.state.parameter?.data;

		if (!FunctionHelper.exists(parameter?.add)) {
			return null;
		}

		let addParameter: PortalAddBarcode = parameter!.add as PortalAddBarcode;

		return (
			<>
				<div className="p-col-12 p-xl-8 p-grid divider-right-hidden">
					{this.renderOutputFormatOptions()}
					{this.renderContentOptions()}

					<fieldset className="p-col-12 p-grid">
						<div className="p-col-12 p-lg-6 p-grid" hidden={
							!this.state.fields?.contains(BarcodeFields.SWISSQRCODE_FORMAT)
						}>
							<label className="p-col-12 p-sm-5" htmlFor="control-swissqrcode-format">
								<Translate {...this.props} data="barcode.create.swissqrcode.format.label"
										   namespace="dialog"/>
							</label>
							<div className="p-col-12 p-sm-7">
								<Dropdown inputId="control-swissqrcode-format"
										  value={FunctionHelper.applyValue(
											  addParameter.qrswiss![0]?.format, QrSwissPaymentBarcode.getFormatDefault()
										  )}
										  onChange={(e) => this.onChange(
											  BarcodeFields.SWISSQRCODE_FORMAT, e.value
										  )}
										  appendTo={document.getElementById("root")}
										  options={[
											  {
												  label: Translate.value(
													  "barcode.create.swissqrcode.format.billonly",
													  {ns: "dialog"}
												  ),
												  value: QrSwissPaymentFormat.BillOnly
											  },
											  {
												  label: Translate.value(
													  "barcode.create.swissqrcode.format.billextraspace",
													  {ns: "dialog"}
												  ),
												  value: QrSwissPaymentFormat.BillExtraSpace
											  },
											  {
												  label: Translate.value(
													  "barcode.create.swissqrcode.format.billportraitsheet",
													  {ns: "dialog"}
												  ),
												  value: QrSwissPaymentFormat.BillPortraitSheet
											  },
										  ]}/>
							</div>
						</div>
						<div className="p-col-12 p-lg-6 p-grid" hidden={
							!this.state.fields?.contains(BarcodeFields.SWISSQRCODE_SEPARATOR) ||
							addParameter.qrswiss![0]?.format === QrSwissPaymentFormat.CodeOnly
						}>
							<label className="p-col-12 p-sm-5" htmlFor="control-swissqrcode-separator">
								<Translate {...this.props} data="barcode.create.swissqrcode.separator.label"
										   namespace="dialog"/>
							</label>
							<div className="p-col-12 p-sm-7">
								<Dropdown inputId="control-swissqrcode-separator"
										  value={FunctionHelper.applyValue(
											  addParameter.qrswiss![0]?.separator,
											  QrSwissPaymentBarcode.getSeparatorDefault()
										  )}
										  onChange={(e) => this.onChange(
											  BarcodeFields.SWISSQRCODE_SEPARATOR, e.value
										  )}
										  appendTo={document.getElementById("root")} options={[
									{
										label: Translate.value(
											"barcode.create.swissqrcode.separator.none", {ns: "dialog"}
										),
										value: QrSwissPaymentSeparator.None
									},
									{
										label: Translate.value(
											"barcode.create.swissqrcode.separator.solidline", {ns: "dialog"}
										),
										value: QrSwissPaymentSeparator.SolidLine
									},
									{
										label: Translate.value(
											"barcode.create.swissqrcode.separator.solidlinewithscissors",
											{ns: "dialog"}
										),
										value: QrSwissPaymentSeparator.SolidLineWithScissors
									},
									{
										label: Translate.value(
											"barcode.create.swissqrcode.separator.dottedline",
											{ns: "dialog"}
										),
										value: QrSwissPaymentSeparator.DottedLine
									},
									{
										label: Translate.value(
											"barcode.create.swissqrcode.separator.dottedlinewithscissors",
											{ns: "dialog"}
										),
										value: QrSwissPaymentSeparator.DottedLineWithScissors
									},
									{
										label: Translate.value(
											"barcode.create.swissqrcode.separator.dashedline",
											{ns: "dialog"}
										),
										value: QrSwissPaymentSeparator.DashedLine
									},
									{
										label: Translate.value(
											"barcode.create.swissqrcode.separator.dashedlinewithscissors",
											{ns: "dialog"}
										),
										value: QrSwissPaymentSeparator.DashedLineWithScissors
									}
								]}/>
							</div>
						</div>
						<div className="p-col-12 p-lg-6 p-grid" hidden={
							!this.state.fields?.contains(BarcodeFields.SWISSQRCODE_POSITION) ||
							addParameter.qrswiss![0]?.format === QrSwissPaymentFormat.BillPortraitSheet
						}>
							<label className="p-col-12 p-sm-5" htmlFor="control-swissqrcode-position">
								<Translate {...this.props} data="barcode.create.swissqrcode.position.label"
										   namespace="dialog"/>
							</label>
							<div className="p-col-12 p-sm-7">
								<Dropdown inputId="control-swissqrcode-position"
										  value={FunctionHelper.applyValue(
											  addParameter.position, PortalAddBarcode.getPositionDefault()
										  )}
										  onChange={(e) => this.onChange(
											  BarcodeFields.SWISSQRCODE_POSITION, e.value
										  )}
										  appendTo={document.getElementById("root")}
										  options={[
											  {
												  label: Translate.value(
													  "barcode.create.swissqrcode.position.top",
													  {ns: "dialog"}
												  ),
												  value: SwissQrCodePosition.top
											  },
											  {
												  label: Translate.value(
													  "barcode.create.swissqrcode.position.center",
													  {ns: "dialog"}
												  ),
												  value: SwissQrCodePosition.center
											  },
											  {
												  label: Translate.value(
													  "barcode.create.swissqrcode.position.bottom",
													  {ns: "dialog"}
												  ),
												  value: SwissQrCodePosition.bottom
											  },
										  ]}/>
							</div>
						</div>

						{this.renderSwissQrCodeOptions()}
					</fieldset>

					<fieldset className="p-col-12 p-grid">
						{this.renderGeneralOptions()}

						<div className="p-col-12 p-lg-6 p-grid" hidden={
							!this.state.fields?.contains(BarcodeFields.PAGEMODE)
						}>
							<label className="p-col-12 p-sm-5" htmlFor="control-swissqrcode-page-mode">
								<Translate {...this.props} data="barcode.pages.mode.label" namespace="dialog"/>
							</label>
							<div className="p-col-12 p-sm-7">
								<Dropdown inputId="control-swissqrcode-page-mode"
										  value={FunctionHelper.applyValue(
											  addParameter.qrswiss![0]?.pdf!.mode, QrSwissPaymentPdf.getModeDefault()
										  )}
										  onChange={(e) => this.onChange(
											  BarcodeFields.PAGEMODE, e.value
										  )}
										  appendTo={document.getElementById("root")}
										  options={[
											  {
												  label: Translate.value("barcode.pages.mode.before",
													  {ns: "dialog"}),
												  value: QrSwissPaymentPdfMode.InsertBefore
											  },
											  {
												  label: Translate.value("barcode.pages.mode.end",
													  {ns: "dialog"}),
												  value: QrSwissPaymentPdfMode.AppendAtEnd
											  }
										  ]}/>
							</div>
						</div>
						<div className="p-col-12 p-lg-6 p-grid" hidden={
							addParameter.format !== PortalBarcodeFormat.swissqrcode ||
							!this.state.fields?.contains(BarcodeFields.PAGERANGE) ||
							addParameter.qrswiss![0]?.pdf!.mode === QrSwissPaymentPdfMode.AppendAtEnd
						}>
							<label className="p-col-12 p-sm-5" htmlFor="control-swissqrcode-page">
								<Translate {...this.props} data="barcode.pages.label" namespace="dialog"/>
							</label>
							<div className="p-col-12 p-sm-7">
								<InputNumber inputId="control-swissqrcode-page" min={1} step={1} max={this.state.pages}
											 useGrouping={false} showButtons={true}
											 value={FunctionHelper.applyValue(
												 addParameter.qrswiss![0]?.pdf?.page, QrSwissPaymentPdf.getPageDefault()
											 )}
											 onChange={(e) => this.onChange(
												 BarcodeFields.PAGERANGE, e.value
											 )}/>
							</div>
						</div>
					</fieldset>
				</div>

				{this.renderPreview()}
			</>
		);
	}

	protected renderContentOptions(): ReactNode {
		let parameter: PortalBarcode | undefined = this.state.parameter?.data;
		let errors: Map<BarcodeFields, string> = this.state.fieldErrors;

		if (!FunctionHelper.exists(parameter?.add)) {
			return null;
		}

		let addParameter: PortalAddBarcode = parameter!.add as PortalAddBarcode;

		return (
			<fieldset className="p-col-12 p-grid" hidden={
				!this.state.fields?.contains(BarcodeFields.CONTENT)
			}>
				<div className="p-col-12 p-lg-6 p-grid">
					<label className="p-col-12 p-sm-5" htmlFor="control-content-format">
						<Translate {...this.props} data="barcode.create.format.label" namespace="dialog"/>
					</label>
					<div className="p-col-12 p-sm-7 p-grid">
						<Dropdown inputId="control-content-format"
								  value={FunctionHelper.applyValue(
									  addParameter.qrswiss![0]?.content?.format,
									  QrSwissPaymentContent.getFormatDefault()
								  )}
								  options={[
									  {
										  label: Translate.value(
											  "barcode.create.format.plain", {ns: "dialog"}
										  ),
										  value: QrSwissPaymentContentFormat.Plain
									  },
									  {
										  label: Translate.value(
											  "barcode.create.format.json", {ns: "dialog"}
										  ),
										  value: QrSwissPaymentContentFormat.Json
									  }
								  ]}
								  onChange={(e) => this.onChange(
									  BarcodeFields.SWISSQRCODE_CONTENT_FORMAT, e.value
								  )}
								  appendTo={document.getElementById("root")}/>
					</div>
				</div>
				<div className="p-col-12 p-lg-6 p-grid">
					<label className="p-col-12 p-sm-5">
						<Translate {...this.props} data="barcode.create.content" namespace="dialog"/>
					</label>
					<div className="p-col-12 p-sm-7 p-grid">
						<Button id="control-content-swiss-content" icon="icon icon-list small"
								label={Translate.value("barcode.create.form", {ns: "dialog"})}
								className="dialog-button p-col-12" data-tooltip-id="tooltip"
								data-tooltip-content={
									errors.has(BarcodeFields.CONTENT) ? Translate.value(
										errors.get(BarcodeFields.CONTENT)!, {ns: "message"}
									) : ""
								}
								onClick={() => this.setState({
									showFormDialog: true
								})}/>
						{this.renderFieldError(errors.has(BarcodeFields.CONTENT) ? Translate.value(
							errors.get(BarcodeFields.CONTENT)!, {ns: "message"}
						) : "")}
					</div>
				</div>
			</fieldset>
		);
	}

	public renderDialog(): ReactNode {
		let parameter: PortalBarcode = this.state.parameter!.data;
		let addParameter: PortalAddBarcode = parameter.add as PortalAddBarcode;

		let swisscodeFormat: QrSwissPaymentContentFormat = FunctionHelper.applyValue(
			addParameter.qrswiss![0]?.content?.format, QrSwissPaymentContent.getFormatDefault()
		);

		let classList: Array<string> = ["create-barcode-dialog", "verylarge"];
		if (this.state.submitted) {
			classList.push("submitted");
		}

		return (
			<>
				<Dialog className={classList.join(" ")} visible={this.props.visible} onHide={this.hide}
						appendTo={document.getElementById("root")} resizable={false}
						closeOnEscape={true} header={this.renderHeader()} footer={this.renderFooter()}>
					<div className="p-grid">
						{this.renderError()}
						<Helper {...this.props}>
							<Translate {...this.props} data={"dialog.barcode.swiss.create.general"} namespace={"help"}
									   markdown={true}/>
						</Helper>
						{this.renderContents()}
					</div>
				</Dialog>
				<BarcodeSwissQrCodeFormDialog {...this.props} visible={this.state.showFormDialog}
											  parameter={this.state.parameter} format={swisscodeFormat}
											  submit={(value: SwissQrCodeForm) => {
												  this.onChange(
													  BarcodeFields.CONTENT, value.toSwissQrCode(
														  swisscodeFormat, true
													  )
												  )
											  }}
											  hide={() => this.setState({
												  showFormDialog: false
											  })}/>
			</>
		);
	}

	protected async onChange(field: BarcodeFields, value: any): Promise<void> {
		await super.onChange(field, value);

		let parameter: PortalBarcode = this.state.parameter!.data;
		let addParameter: PortalAddBarcode = parameter.add as PortalAddBarcode;
		let errors: Map<BarcodeFields, string> = this.state.fieldErrors;

		switch (field) {
			case BarcodeFields.SWISSQRCODE_FORMAT:
				this.onChange(BarcodeFields.SWISSQRCODE_POSITION, addParameter.position);
				break;
			case BarcodeFields.CONTENT:
				if (!FunctionHelper.exists(value) || FunctionHelper.isEmpty(value)) {
					errors.set(field, "errors.fields.set");
				} else if (errors.has(field)) {
					errors.delete(field);
				}

				addParameter.qrswiss![0].content!.value = value;
				break;
			case BarcodeFields.PAGEMODE:
				addParameter.qrswiss![0].pdf!.mode = value;
				break;
			case BarcodeFields.PAGERANGE:
				if (!FunctionHelper.exists(value) || FunctionHelper.isEmpty(value)) {
					value = 1;
				} else if (value > this.state.pages) {
					value = this.state.pages;
				}

				addParameter.qrswiss![0].pdf!.page = value;
				break;
			case BarcodeFields.OUTPUT:
				addParameter.outputFormat = value;
				break;
			case BarcodeFields.SWISSQRCODE_POSITION:
				addParameter.position = value;

				if (addParameter.qrswiss![0].format === QrSwissPaymentFormat.BillPortraitSheet) {
					addParameter.qrswiss![0].position = undefined;
					break;
				}

				if (!FunctionHelper.exists(addParameter.qrswiss![0].position)) {
					addParameter.qrswiss![0].position = Rectangle.fromJson({
						metrics: Metrics.Mm,
						x: 0,
						y: 0,
						width: 0,
						height: 0
					} as Rectangle);
				}

				switch (value) {
					case SwissQrCodePosition.top:
						addParameter.qrswiss![0].position!.y = 0;
						break;
					case SwissQrCodePosition.center:
						addParameter.qrswiss![0].position!.y =
							(this.state.pageHeight / 2) - (this.getFormatHeight(addParameter.qrswiss![0].format!) / 2);
						break;
					case SwissQrCodePosition.bottom:
						addParameter.qrswiss![0].position!.y =
							this.state.pageHeight - this.getFormatHeight(addParameter.qrswiss![0].format!);
						break;
				}
				break;
		}

		this.setState({
			parameter: this.state.parameter
		});
	}

	protected renderHeader(): ReactNode {
		return (
			<div>
				<h1>
					<Icon icon={"icon-barcode-swiss"}/>
					<Translate {...this.props} data="barcode.swissqrcode.title" namespace="dialog"/>
				</h1>
				<Button icon="icon icon-close" onClick={() => this.hide()}/>
				{this.renderHelpButton()}
			</div>
		);
	}

	/**
	 * Handle showing/initializing the dialog
	 */
	protected async onShow(): Promise<void> {
		await super.onShow();

		let parameter: PortalBarcode | undefined = this.state.parameter?.data;
		let portalDocument: PortalDocument = this.props.context.documentManager.getSelection()[0];
		let documentSettings: DocumentSettings = portalDocument.getSettings();
		let pageCount: number = await portalDocument.getNumberOfPages();
		let currentPage: number = documentSettings.activeViewer.currentPage;

		if (!FunctionHelper.exists(parameter?.add)) {
			return;
		}

		let addParameter: PortalAddBarcode = parameter!.add as PortalAddBarcode;
		addParameter.qrswiss![0].pdf!.page = currentPage;

		let pageMetadata: PagePages = portalDocument.getPage(currentPage)!;
		let pageHeight: number = UnitHelper.unitToUnit(
			FunctionHelper.applyValue(pageMetadata.height, PagePages.getHeightDefault), LengthUnit.POINTS,
			LengthUnit.MILLIMETER, this.props.portalResolution
		);

		this.setState({
			parameter: this.state.parameter,
			defaultParameter: this.state.parameter?.clone(),
			currentPage: currentPage.toString(),
			pages: pageCount,
			pageHeight: pageHeight
		});
	};

	protected async submit(): Promise<void> {
		await super.submit();

		let operationData: PortalBarcode = this.state.parameter!.data.clone();

		if ((operationData.add as PortalAddBarcode).format === PortalBarcodeFormat.swissqrcode) {
			operationData.add!.qrswiss![0].content!.value =
				Base64.toBase64(operationData.add!.qrswiss![0].content!.value!);
		}

		let webServiceType: WebServiceType = WebServiceTypes.BARCODE;
		let documentManager: PortalDocumentManager = this.props.context.documentManager;
		let selection: Array<PortalDocument> = documentManager.getSelection();

		for (let portalDocument of selection) {
			try {
				await this.props.context.session.executeRequest({
					webServiceType: webServiceType,
					document: portalDocument,
					operation: Barcode.fromJson(operationData.toJson()),
					settings: Settings.fromJson({
						pdfa: {
							pdfaEditProfile: this.props.pdfaEditProfile
						},
						signature: {
							signatureEditProfile: this.props.signatureEditProfile
						},
						compress: {
							compressProfile: this.props.compressProfile,
							compressObjects: {
								objectStreamSize: this.props.objectStreamSize
							}
						}
					} as Settings)
				});
			} catch (exception: any) {
				if (await this.onError(exception)) {
					await this.submit();
				}
				return;
			}

			let message: Message = new Message("success.barcode.create", MessageType.SUCCESS);
			message.context = {filename: '"' + portalDocument.getFileName() + '" '};
			this.props.context.getPage().addMessage(message);
		}

		this.resetMouseMode(documentManager.getSelection());
		this.hide();
	}

	protected getDialogType(): PortalDialogType {
		return PortalDialogType.BARCODE_CREATE_SWISS_QR;
	}

	protected async resetParameter(): Promise<void> {
		await super.resetParameter();

		let portalDocument: PortalDocument = this.props.context.documentManager.getSelection()[0];
		let currentPage: number = portalDocument.getSettings().activeViewer.currentPage;
		let pageMetadata: PagePages = portalDocument.getPage(currentPage)!;
		let pageHeight: number = UnitHelper.unitToUnit(
			FunctionHelper.applyValue(pageMetadata.height, PagePages.getHeightDefault), LengthUnit.POINTS,
			LengthUnit.MILLIMETER, this.props.portalResolution
		);

		this.setState({
			pageHeight: pageHeight
		});
	}
}

const BarcodeCreateSwissQrCodeDialog = React.memo(BarcodeCreateSwissQrCodeDialogDefinition,
	(
		prevProps: Readonly<BarcodeCreateSwissQrCodeDialogProperties>,
		nextProps: Readonly<BarcodeCreateSwissQrCodeDialogProperties>
	) => {
		if (!nextProps.visible && !prevProps.visible) {
			return true
		}

		return false;
	}
);

export default BarcodeCreateSwissQrCodeDialog;